import { useEffect, useState } from "react";
import { Calendar, MapPin, Download, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCents } from "@/lib/money";
import { formatDate } from "@/lib/dates";
import { qrDataUrl } from "@/lib/qr";

const STATUS_LABEL = { completed: "completed", refunded: "refunded", partially_refunded: "partially refunded", pending: "pending", failed: "failed" };

export default function TicketCard({ order, printMode = false }) {
  const [downloading, setDownloading] = useState(false);
  const [qr, setQr] = useState(null);
  const code = order.ticket_code || order.id;
  const isGood = order.status === "completed" || order.status === "partially_refunded";
  const eventDate = order.event_date ? formatDate(order.event_date, "weekdayLong") : "";

  useEffect(() => {
    let alive = true;
    qrDataUrl(code, 220).then((url) => alive && setQr(url)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [code]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { jsPDF } = await import("jspdf");
      const qrBase64 = qr ?? (await qrDataUrl(code, 220).catch(() => null));

      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 40;
      const cardW = pageW - margin * 2;

      pdf.setFillColor(79, 70, 229);
      pdf.roundedRect(margin, 40, cardW, 90, 8, 8, "F");

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(20);
      pdf.setFont("helvetica", "bold");
      const titleLines = pdf.splitTextToSize(order.event_title || "Event", cardW - 20);
      pdf.text(titleLines, margin + 16, 72);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(`${order.quantity || 1} ${(order.quantity || 1) === 1 ? "Ticket" : "Tickets"}`, margin + 16, 118);

      const statusText = (STATUS_LABEL[order.status] || order.status || "").toUpperCase();
      if (isGood) pdf.setFillColor(74, 222, 128);
      else pdf.setFillColor(234, 179, 8);
      pdf.roundedRect(margin + cardW - 80, 50, 72, 18, 4, 4, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.text(statusText, margin + cardW - 80 + 36, 62, { align: "center" });

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineDashPattern([4, 3], 0);
      pdf.line(margin, 148, margin + cardW, 148);
      pdf.setLineDashPattern([], 0);

      let y = 172;
      pdf.setTextColor(55, 65, 81);
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");

      if (eventDate) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Date:", margin + 16, y);
        pdf.setFont("helvetica", "normal");
        pdf.text(eventDate, margin + 60, y);
        y += 22;
      }
      if (order.event_location) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Location:", margin + 16, y);
        pdf.setFont("helvetica", "normal");
        const locLines = pdf.splitTextToSize(order.event_location, cardW - 220);
        pdf.text(locLines, margin + 80, y);
        y += 22 * locLines.length;
      }
      if (order.ticket_option_name) {
        pdf.setFont("helvetica", "bold");
        pdf.text("Ticket Type:", margin + 16, y);
        pdf.setFont("helvetica", "normal");
        pdf.text(order.ticket_option_name, margin + 100, y);
        y += 22;
      }

      y += 8;
      pdf.setDrawColor(229, 231, 235);
      pdf.line(margin + 16, y, margin + cardW - 16, y);
      y += 16;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(156, 163, 175);
      pdf.text("TICKET CODE", margin + 16, y);
      y += 14;
      pdf.setFont("courier", "bold");
      pdf.setFontSize(13);
      pdf.setTextColor(31, 41, 55);
      pdf.text(code, margin + 16, y);
      y += 22;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(156, 163, 175);
      pdf.text("TOTAL PAID", margin + 16, y);
      y += 14;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(31, 41, 55);
      pdf.text(formatCents(order.total_cents), margin + 16, y);

      if (qrBase64) {
        pdf.addImage(qrBase64, "PNG", margin + cardW - 120, 162, 104, 104);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(156, 163, 175);
        pdf.text("Scan at entry", margin + cardW - 120 + 52, 278, { align: "center" });
      }

      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(1);
      pdf.roundedRect(margin, 40, cardW, Math.max(y + 24, 310) - 40, 8, 8, "S");

      pdf.save(`ticket-${code}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-2 ticket-card">
      <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-xl leading-tight">{order.event_title}</h3>
              <Badge className="mt-2 bg-white/20 text-white border-white/30 text-xs">
                {order.quantity} {order.quantity === 1 ? "Ticket" : "Tickets"}
              </Badge>
            </div>
            <Badge className={`border-0 text-white text-xs ${isGood ? "bg-green-400" : "bg-yellow-400"}`}>
              {STATUS_LABEL[order.status] || order.status}
            </Badge>
          </div>
        </div>

        <div className="relative px-6">
          <div className="border-t-2 border-dashed border-gray-200" />
          <div className="absolute left-0 top-0 w-5 h-5 bg-gray-50 rounded-full -translate-x-2.5 -translate-y-2.5 border border-gray-200" />
          <div className="absolute right-0 top-0 w-5 h-5 bg-gray-50 rounded-full translate-x-2.5 -translate-y-2.5 border border-gray-200" />
        </div>

        <div className="p-5 flex gap-5 items-start">
          <div className="flex-1 space-y-2 min-w-0">
            {eventDate && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                <span>{eventDate}</span>
              </div>
            )}
            {order.event_location && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                <span className="truncate">{order.event_location}</span>
              </div>
            )}
            {order.ticket_option_name && (
              <p className="text-sm text-indigo-600 font-medium">{order.ticket_option_name}</p>
            )}
            <div className="pt-3 border-t border-gray-100 space-y-1">
              <div>
                <p className="text-xs text-gray-400">Ticket Code</p>
                <p className="font-mono text-sm font-bold text-gray-700 truncate">{code}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Total Paid</p>
                <p className="font-bold text-gray-900">{formatCents(order.total_cents)}</p>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col items-center gap-1">
            {qr ? (
              <img src={qr} alt={`QR code for ${code}`} className="w-[110px] h-[110px] rounded" />
            ) : (
              <div className="w-[110px] h-[110px] rounded bg-gray-100" />
            )}
            <p className="text-xs text-gray-400">Scan at entry</p>
          </div>
        </div>
      </div>

      {!printMode && (
        <Button onClick={handleDownload} disabled={downloading} variant="outline" className="w-full gap-2 text-sm print:hidden">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {downloading ? "Generating PDF..." : "Download Ticket"}
        </Button>
      )}
    </div>
  );
}
